import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap, Crown, Star, CalendarPlus, Trophy, Gift, Ticket,
  Clock, ChevronRight, Sparkles, MessagesSquare, BarChart3, UserRound,
} from "lucide-react";
import apiClient from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { FullPageLoader, SectionCard } from "@/components/common";

const StatTile = ({ icon: Icon, label, value, color }) => (
  <SectionCard className="flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 icon-tile"
      style={{ background: `${color}1f`, border: `1px solid ${color}55`, "--tile-ic": color }}>
      <Icon className="w-6 h-6" style={{ color }} />
    </div>
    <div>
      <p className="text-slate-400 text-xs uppercase tracking-widest">{label}</p>
      <p className="font-display text-2xl font-bold text-white">{value}</p>
    </div>
  </SectionCard>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [membership, setMembership] = useState({ subscription: null, plan: null });
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [me, mem, act] = await Promise.all([
          refreshUser(),
          apiClient.get("/memberships/my"),
          apiClient.get("/users/activity"),
        ]);
        setMembership(mem.data);
        setActivity(act.data);
      } catch (e) { /* ignore */ } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <FullPageLoader label="Loading Dashboard" />;

  const plan = membership?.plan;
  const isMember = !!membership?.subscription && !!plan;

  const quickActions = [
    { label: "Book a Station", desc: "Reserve your rig", icon: CalendarPlus, to: "/booking", color: "#00e5ff" },
    { label: "Tournaments", desc: "Compete & win", icon: Trophy, to: "/tournaments", color: "#ffb020" },
    { label: "Redeem Rewards", desc: "Spend your points", icon: Gift, to: "/rewards", color: "#00ff9d" },
    { label: "Chat", desc: "Talk with the crew", icon: MessagesSquare, to: "/community", color: "#8b5cf6" },
  ];

  const tierColor = { basic: "#00e5ff", pro: "#00ff9d", champ: "#ffb020", vip: "#8b5cf6" };
  const memberColor = tierColor[plan?.type] || "#8b5cf6";

  const recentBookings = activity?.recent_bookings || [];

  return (
    <div className="arena-bg">
      <div className="mb-8 animate-in-up">
        <p className="text-slate-400 uppercase tracking-widest text-sm">Welcome back, driver</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-white uppercase">
          {user?.first_name} <span className="neon-cyan-text">{user?.last_name}</span>
        </h1>
      </div>

      {/* Membership hero card */}
      <div className="glass rounded-2xl p-6 sm:p-8 mb-8 relative overflow-hidden animate-in-up"
        style={{ border: isMember ? "1px solid rgba(139,92,246,0.5)" : "1px solid rgba(120,150,220,0.12)" }}
        data-testid="membership-card">
        <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full blur-3xl"
          style={{ background: isMember ? "rgba(139,92,246,0.28)" : "rgba(0,229,255,0.14)" }} />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <div className="chip mb-3"
              style={isMember
                ? { background: `${memberColor}1f`, border: `1px solid ${memberColor}80`, color: memberColor }
                : { background: "rgba(120,150,220,0.12)", border: "1px solid rgba(120,150,220,0.3)", color: "#94a3b8" }}>
              {isMember
                ? <Crown className="w-3.5 h-3.5 tier-crown" style={{ "--tier-ic": memberColor }} />
                : <UserRound className="w-3.5 h-3.5" />} {isMember ? plan.name : "Free Member"}
            </div>
            <h2 className="font-display text-2xl font-bold text-white">
              {isMember ? plan.name : "Standard Access"}
            </h2>
            {isMember ? (
              <p className="text-slate-300 mt-1">
                {plan.discount_percentage}% session discount · {plan.points_multiplier}x points multiplier
              </p>
            ) : (
              <p className="text-slate-400 mt-1">Upgrade to unlock discounts, bonus points & VIP perks.</p>
            )}
          </div>
          <button onClick={() => navigate("/membership")}
            className={isMember ? "neon-outline-btn px-6 py-3" : "neon-btn px-6 py-3"}
            data-testid="manage-membership-btn">
            {isMember ? "Manage" : "Upgrade Now"} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {isMember && plan?.benefits?.length > 0 && (
          <div className="relative mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-2">
            {plan.benefits.map((b, i) => (
              <span key={i} className="chip" style={{ background: "rgba(0,255,157,0.1)", border: "1px solid rgba(0,255,157,0.3)", color: "#00ff9d" }}>
                <Sparkles className="w-3 h-3" /> {b}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-5 mb-8">
        <StatTile icon={Zap} label="Points Balance" value={(user?.points ?? 0).toLocaleString()} color="#00e5ff" />
        <StatTile icon={Star} label="Free Gaming Hours" value={user?.free_session_credits ?? 0} color="#00ff9d" />
        <button onClick={() => navigate("/leaderboards")} data-testid="dash-leaderboard-card"
          className="glass glass-hover rounded-2xl p-4 flex items-center gap-4 text-left w-full">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 icon-tile"
            style={{ background: "#ffb0201f", border: "1px solid #ffb02055", "--tile-ic": "#ffb020" }}>
            <BarChart3 className="w-6 h-6" style={{ color: "#ffb020" }} />
          </div>
          <div className="min-w-0">
            <p className="text-slate-400 text-xs uppercase tracking-widest">Leaderboard</p>
            <p className="font-display text-lg font-bold text-white leading-tight">See your rank</p>
            <span className="text-xs font-semibold flex items-center gap-1" style={{ color: "#ffb020" }}>
              View leaderboards <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </button>
      </div>

      {/* Quick actions */}
      <h3 className="font-display text-lg font-bold text-white uppercase tracking-wide mb-4">Quick Actions</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {quickActions.map((a) => (
          <button key={a.to} onClick={() => navigate(a.to)}
            className="glass glass-hover rounded-2xl p-6 text-left"
            data-testid={`quick-${a.to.replace("/", "")}`}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 icon-tile"
              style={{ background: `${a.color}1f`, border: `1px solid ${a.color}55`, "--tile-ic": a.color }}>
              <a.icon className="w-6 h-6" style={{ color: a.color }} />
            </div>
            <p className="font-display font-bold text-white">{a.label}</p>
            <p className="text-slate-400 text-sm">{a.desc}</p>
          </button>
        ))}
      </div>

      {/* Recent activity */}
      <h3 className="font-display text-lg font-bold text-white uppercase tracking-wide mb-4">Recent Bookings</h3>
      <SectionCard>
        {recentBookings.length === 0 ? (
          <div className="text-center py-8">
            <Ticket className="w-10 h-10 mx-auto mb-3 text-slate-500" />
            <p className="text-slate-400 mb-4">No bookings yet. Reserve your first session!</p>
            <button onClick={() => navigate("/booking")} className="neon-btn px-6 py-3 mx-auto">
              <CalendarPlus className="w-4 h-4" /> Book Now
            </button>
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {recentBookings.slice(0, 5).map((b) => (
              <div key={b.id} className="flex items-center justify-between py-3.5" data-testid={`dash-booking-${b.id}`}>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="text-white font-semibold">
                      {new Date(b.start_time).toLocaleDateString(undefined, { month: "short", day: "numeric" })} ·{" "}
                      {new Date(b.start_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                    </p>
                    <p className="text-slate-400 text-sm">{b.station_ids?.length || 1} station(s) · ${b.total_amount?.toFixed(2)}</p>
                  </div>
                </div>
                <span className="chip capitalize" style={{
                  background: b.status === "confirmed" ? "rgba(0,255,157,0.12)" : "rgba(255,176,32,0.12)",
                  border: `1px solid ${b.status === "confirmed" ? "rgba(0,255,157,0.4)" : "rgba(255,176,32,0.4)"}`,
                  color: b.status === "confirmed" ? "#00ff9d" : "#ffb020",
                }}>{b.status}</span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
